import { useState, useEffect } from 'react';
import { checkIpBeforeCheckin, recordCheckin, getEmployeeCheckins } from '../api/checkin';
import CalendarView from './CalendarView';
import ListView from './ListView';
import IpInfo from './IpInfo';
import { LeaveRequestForm } from './LeaveRequestForm';
import type { IpInfoData } from './IpInfo';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

// Initialize dayjs plugins
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault('Asia/Bangkok');

interface CheckinFormProps {
  employeeData: {
    id: string;
    name: string;
    recoveryCode: string;
  };
  onLogout: () => void;
}

const CheckinForm = ({ employeeData, onLogout }: CheckinFormProps) => {
  const [view, setView] = useState<'calendar' | 'list'>('calendar');
  const [checkinStatus, setCheckinStatus] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [checkins, setCheckins] = useState<Array<any>>([]);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isTimeRestricted, setIsTimeRestricted] = useState(false);
  const [ipInfo, setIpInfo] = useState<IpInfoData | null>(null);
  const [lateStatus, setLateStatus] = useState('');
  const [mealAllowance, setMealAllowance] = useState(0);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [todayCheckin, setTodayCheckin] = useState<any>(null);
  const [hasCheckedInToday, setHasCheckedInToday] = useState(false);

  useEffect(() => {
    // Load check-in history and check today's status
    loadCheckinHistory();

    // Check for existing check-in status
    const checkedInToday = localStorage.getItem('checkedInToday');
    if (checkedInToday) {
      try {
        const { date, status } = JSON.parse(checkedInToday);
        const today = dayjs().tz('Asia/Bangkok').format('YYYY-MM-DD');
        if (date === today && status) {
          setHasCheckedInToday(true);
          setCheckinStatus('You have already checked in today');
        }
      } catch (error) {
        console.error('Error parsing checkedInToday:', error);
        localStorage.removeItem('checkedInToday');
      }
    }

    // Update current time every second
    const timer = setInterval(() => {
      const now = dayjs().tz('Asia/Bangkok').toDate();
      setCurrentTime(now);
      
      // Check if current time is within allowed check-in hours (6:00 AM - 5:00 PM)
      const hours = now.getHours();
      setIsTimeRestricted(hours < 6 || hours >= 17);
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const loadCheckinHistory = async () => {
    try {
      const history = await getEmployeeCheckins(employeeData.id);
      console.log('Checkin history response:', history);
      
      // Find today's check-in
      const today = dayjs().tz('Asia/Bangkok').format('YYYY-MM-DD');
      const todayRecord = Array.isArray(history) ? 
        history.find(record => dayjs(record.created_at).tz('Asia/Bangkok').format('YYYY-MM-DD') === today) : null;
      
      if (todayRecord) {
        setTodayCheckin(todayRecord);
        setHasCheckedInToday(true);
        setLateStatus(todayRecord.late_status);
        setMealAllowance(todayRecord.meal_allowance);
      }
      
      // Ensure history is an array before setting state
      setCheckins(Array.isArray(history) ? history : []);
    } catch (error) {
      console.error('Error loading history:', error);
      setCheckins([]);
      setError('Failed to load check-in history. Please try again later.');
    }
  };

  const handleCheckin = async () => {
    setError('');
    setCheckinStatus('');
    setLoading(true);

    try {
      // Check IP information before check-in
      const { confirmed, ipInfo: newIpInfo } = await checkIpBeforeCheckin();
      setIpInfo(newIpInfo);

      if (!confirmed) {
        setError('Check-in cancelled. Please connect to the shop WiFi and try again.');
        return;
      }

      const result = await recordCheckin(employeeData.id);

      if (result.status === 'time_restricted') {
        setIsTimeRestricted(true);
        setError('Check-in is not allowed at this time.');
        return;
      }

      if (result.status === 'already_checked_in') {
        setCheckinStatus('You have already checked in today');
        setHasCheckedInToday(true);
        // Save checked-in status to localStorage
        localStorage.setItem('checkedInToday', JSON.stringify({
          date: dayjs().tz('Asia/Bangkok').format('YYYY-MM-DD'),
          status: true
        }));
      } else {
        setCheckinStatus(`Check-in successful at ${dayjs().tz('Asia/Bangkok').format('HH:mm:ss')}`);
        setHasCheckedInToday(true);
        // Save checked-in status to localStorage
        localStorage.setItem('checkedInToday', JSON.stringify({
          date: dayjs().tz('Asia/Bangkok').format('YYYY-MM-DD'),
          status: true
        }));

        // Set late status and meal allowance
        setLateStatus(result.lateStatus.status);
        setMealAllowance(result.mealAllowance);
      }

      // Refresh history
      await loadCheckinHistory();
    } catch (error) {
      console.error('Check-in error:', error);
      setError(`Error checking in: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      // Clear login data
      localStorage.removeItem('employeeData');
      localStorage.removeItem('checkedInToday');
      onLogout();
    }
  };

  return (
    <div className="mt-3">
      <button 
        onClick={handleLogout}
        className="absolute top-2 right-2 py-1 px-2 text-xs rounded border border-blue-500 text-blue-500 hover:bg-blue-50 opacity-60 hover:opacity-100 transition-opacity hidden md:block"
      >
        Logout
      </button>

      <div className="text-green-500 font-medium mb-4">Welcome, {employeeData.name}!</div>

      <div>
        <button 
          onClick={handleCheckin}
          disabled={loading || isTimeRestricted || hasCheckedInToday}
          className={`w-full py-10 sm:py-14 md:py-16 text-2xl sm:text-4xl font-bold rounded-xl md:rounded-2xl shadow-md md:shadow-xl relative ${
            isTimeRestricted || hasCheckedInToday ? 'bg-gray-500 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-600'
          } text-white transition-all duration-300 ease-in-out`}
        >
          {hasCheckedInToday ? 'Already Checked In Today' : 'Check In Now'}
          <div className="text-sm opacity-75 absolute bottom-2 sm:bottom-3 md:bottom-4 left-0 right-0 pt-4">
            {dayjs(currentTime).tz('Asia/Bangkok').format('HH:mm:ss')}
          </div>
        </button>

        {isTimeRestricted && !hasCheckedInToday && (
          <div className="text-amber-500 mt-2 text-center">
            Check-ins are only allowed between 6:00 AM and 5:00 PM
          </div>
        )}

        {todayCheckin && (
          <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-100">
            <h4 className="text-lg font-semibold text-blue-700 mb-2">Today's Check-in Details</h4>
            <div className="space-y-2">
              <p className="text-gray-700">
                Time: {dayjs(todayCheckin.created_at).tz('Asia/Bangkok').format('HH:mm:ss')}
              </p>
              <p className={`font-medium ${
                todayCheckin.late_status === 'perfect_on_time' ? 'text-green-600' :
                todayCheckin.late_status === 'on_time' ? 'text-blue-600' :
                todayCheckin.late_status === 'late_10' ? 'text-amber-600' : 'text-red-600'
              }`}>
                Status: {
                  todayCheckin.late_status === 'perfect_on_time' ? 'Perfect (Before 8:00)' :
                  todayCheckin.late_status === 'on_time' ? 'On Time' :
                  todayCheckin.late_status === 'late_10' ? 'Late (8:10-8:30)' : 'Late (after 8:30)'
                }
              </p>
              {todayCheckin.meal_allowance > 0 && (
                <p className="text-blue-600">
                  🍽️ Meal Allowance: {todayCheckin.meal_allowance} THB
                </p>
              )}
              {todayCheckin.exemption_applied && (
                <p className="text-blue-600">
                  🎉 Weekly exemption applied
                </p>
              )}
            </div>
          </div>
        )}

        {checkinStatus && !todayCheckin && (
          <div className="mt-4">
            <div className="text-green-500 font-medium mb-4">{checkinStatus}</div>
            {lateStatus && (
              <div className="p-2 sm:p-3 rounded bg-gray-100 mt-2">
                <p className={`font-medium ${
                  lateStatus === 'perfect_on_time' ? 'text-green-600' :
                  lateStatus === 'on_time' ? 'text-blue-600' :
                  lateStatus === 'late_10' ? 'text-amber-600' : 'text-red-600'
                }`}>
                  {lateStatus === 'perfect_on_time' && '🌟 Perfect timing! You arrived before 8:00 AM'}
                  {lateStatus === 'on_time' && '✅ On time! You arrived before 8:10 AM'}
                  {lateStatus === 'late_10' && '⚠️ You are late (between 8:10 AM and 8:30 AM)'}
                  {lateStatus === 'late_15' && '❌ You are very late (after 8:30 AM)'}
                </p>
              </div>
            )}
            {mealAllowance > 0 && (
              <div className="p-3 sm:p-4 mt-3 bg-blue-50 border-l-4 border-blue-500 rounded-r-md text-blue-700">
                Congratulations! You received {mealAllowance} THB meal allowance for early check-in! 🍽️
              </div>
            )}
          </div>
        )}

        {error && (
          <div className="mt-4 p-3 bg-red-50 border border-red-100 rounded-lg text-red-600">
            <p className="flex items-center">
              <span className="mr-2">⚠️</span>
              {error}
            </p>
          </div>
        )}

        {loading && (
          <div className="flex items-center justify-center p-4 text-gray-600">
            <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            Processing check-in...
          </div>
        )}

        {ipInfo && <IpInfo info={ipInfo} />}

        <div className="flex justify-center mt-6">
          <button
            onClick={() => setShowLeaveModal(true)}
            className="py-3 px-6 bg-green-500 hover:bg-green-600 text-white rounded-lg shadow-md transition-colors duration-200 flex items-center space-x-2"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
            </svg>
            <span>Request Leave</span>
          </button>
        </div>

        {showLeaveModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
              <div className="p-4 border-b border-gray-200 flex justify-between items-center">
                <h2 className="text-xl font-semibold">Request Leave</h2>
                <button
                  onClick={() => setShowLeaveModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="p-4">
                <LeaveRequestForm employeeId={employeeData.id} />
              </div>
            </div>
          </div>
        )}
      </div>

      <h3 className="text-lg sm:text-xl font-semibold mt-4 sm:mt-6 md:mt-8 mb-2 sm:mb-3 md:mb-4 pb-2 border-b border-gray-200">
        Check-in History
      </h3>

      <div className="view-toggle flex border border-gray-200 rounded-lg overflow-hidden mb-3 sm:mb-4">
        <button 
          className={`flex-1 py-2 px-3 sm:px-4 text-center bg-white text-gray-700 border-r border-gray-200 ${
            view === 'calendar' ? 'bg-blue-50 text-blue-600' : ''
          }`}
          onClick={() => setView('calendar')}
        >
          Calendar View
        </button>
        <button 
          className={`flex-1 py-2 px-3 sm:px-4 text-center bg-white text-gray-700 ${
            view === 'list' ? 'bg-blue-50 text-blue-600' : ''
          }`}
          onClick={() => setView('list')}
        >
          List View
        </button>
      </div>

      {view === 'calendar' ? (
        <CalendarView checkins={checkins} />
      ) : (
        <ListView checkins={checkins} />
      )}
    </div>
  );
};

export default CheckinForm; 